import FilterListIcon from "@mui/icons-material/FilterList";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import RefreshIcon from "@mui/icons-material/Refresh";
import SensorsIcon from "@mui/icons-material/Sensors";
import { Autocomplete, Chip, Popover, TextField } from "@mui/material";
import { useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import { ServiceDashboardMessages } from "@/messages/serviceDashboard/service-dashboard.messages";

export type FilterOption = { id: number; name: string };

type Props = {
  messages: ServiceDashboardMessages;
  queueOptions: FilterOption[];
  tagOptions: FilterOption[];
  workflowOptions: FilterOption[];
  selectedQueues: FilterOption[];
  selectedTags: FilterOption[];
  selectedWorkflows: FilterOption[];
  onChangeQueues: (value: FilterOption[]) => void;
  onChangeTags: (value: FilterOption[]) => void;
  onChangeWorkflows: (value: FilterOption[]) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  autoRefresh: boolean;
  onToggleAutoRefresh: (value: boolean) => void;
  onRefresh: () => void;
  lastUpdatedAt: Date | null;
  isRefreshing: boolean;
  isFullscreen: boolean;
  onFullscreen: () => void;
};

const autocompleteSx = { "& .MuiOutlinedInput-root": { paddingY: "0.15rem" } };

const DashboardHeader = ({
  messages,
  queueOptions,
  tagOptions,
  workflowOptions,
  selectedQueues,
  selectedTags,
  selectedWorkflows,
  onChangeQueues,
  onChangeTags,
  onChangeWorkflows,
  onApplyFilters,
  onClearFilters,
  autoRefresh,
  onToggleAutoRefresh,
  onRefresh,
  lastUpdatedAt,
  isRefreshing,
  isFullscreen,
  onFullscreen,
}: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const formattedUpdate = lastUpdatedAt
    ? new Intl.DateTimeFormat("pt-BR", { hour: "2-digit", minute: "2-digit" }).format(lastUpdatedAt)
    : "--:--";

  const activeFilters =
    selectedQueues.length + selectedTags.length + selectedWorkflows.length || undefined;

  return (
    <MDBox
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
      gap={1}
      mb={2}
    >
      <MDTypography variant="h4" fontWeight="bold">
        {messages.pageTitle}
      </MDTypography>

      <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Chip
          size="small"
          variant="outlined"
          icon={
            <RefreshIcon
              fontSize="small"
              sx={{
                animation: isRefreshing ? "dashSpin 1s linear infinite" : "none",
                "@keyframes dashSpin": { to: { transform: "rotate(360deg)" } },
              }}
            />
          }
          label={`${messages.lastUpdatedLabel} ${formattedUpdate}`}
          sx={{ fontWeight: 600, borderRadius: "0.5rem", height: "2rem", px: 0.5 }}
        />

        <MDButton
          variant={autoRefresh ? "gradient" : "outlined"}
          color={autoRefresh ? "success" : "dark"}
          size="small"
          onClick={() => onToggleAutoRefresh(!autoRefresh)}
          sx={{ textTransform: "none" }}
        >
          <SensorsIcon fontSize="small" />
          &nbsp;{messages.realtimeLabel}
        </MDButton>

        <MDButton
          variant="outlined"
          color="dark"
          size="small"
          onClick={onRefresh}
          sx={{ textTransform: "none" }}
        >
          <RefreshIcon fontSize="small" />
          &nbsp;{messages.refreshButton}
        </MDButton>


        <MDButton
          variant={activeFilters ? "gradient" : "outlined"}
          color={activeFilters ? "primary" : "dark"}
          size="small"
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ textTransform: "none" }}
        >
          <FilterListIcon fontSize="small" />
          &nbsp;{messages.filtersButton}
          {activeFilters ? ` (${activeFilters})` : ""}
        </MDButton>

        {!isFullscreen && (
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            onClick={onFullscreen}
            sx={{ textTransform: "none" }}
          >
            <FullscreenIcon fontSize="small" />
          </MDButton>
        )}
      </MDBox>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: (theme) => ({
              borderRadius: "0.75rem",
              mt: 1,
              backgroundColor: theme.palette.background.paper,
              backgroundImage: "none",
              opacity: 1,
              boxShadow: theme.shadows[8],
            }),
          },
        }}
      >
        <MDBox p={2} width={{ xs: "18rem", sm: "22rem" }} display="grid" gap={1.5}>
          <Autocomplete
            multiple
            size="small"
            sx={autocompleteSx}
            options={queueOptions}
            getOptionLabel={(option) => option.name}
            value={selectedQueues}
            onChange={(_, value) => onChangeQueues(value as FilterOption[])}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={messages.queueFilter} />}
          />
          <Autocomplete
            multiple
            size="small"
            sx={autocompleteSx}
            options={tagOptions}
            getOptionLabel={(option) => option.name}
            value={selectedTags}
            onChange={(_, value) => onChangeTags(value as FilterOption[])}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={messages.tagFilter} />}
          />
          <Autocomplete
            multiple
            size="small"
            sx={autocompleteSx}
            options={workflowOptions}
            getOptionLabel={(option) => option.name}
            value={selectedWorkflows}
            onChange={(_, value) => onChangeWorkflows(value as FilterOption[])}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={messages.workflowFilter} />}
          />
          <MDBox display="flex" gap={1}>
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              fullWidth
              sx={{ textTransform: "none" }}
              onClick={onClearFilters}
            >
              {messages.clearFiltersButton}
            </MDButton>
            <MDButton
              variant="gradient"
              color="primary"
              size="small"
              fullWidth
              sx={{ textTransform: "none" }}
              onClick={() => {
                onApplyFilters();
                setAnchorEl(null);
              }}
            >
              {messages.applyFiltersButton}
            </MDButton>
          </MDBox>
        </MDBox>
      </Popover>
    </MDBox>
  );
};

export default DashboardHeader;
